import { DateTime } from 'luxon';
import { sessionManager } from './session-manager';

export abstract class BaseApi {
  protected readonly gateway: string;

  constructor(gateway: string) {
    this.gateway = gateway;
    sessionManager.setGateway(gateway);
  }

  protected async _request<T>(
    uri: string,
    options: RequestInit = {},
    behavior?: { skipIsUpdating?: boolean },
  ): Promise<T> {
    if (!behavior?.skipIsUpdating) await sessionManager.getIsUpdating();

    const headers = new Headers(options.headers || {});

    try {
      const response = await fetch(`${this.gateway}/api${uri}`, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401) {
          sessionManager.reset();
          // Clear persisted user store to prevent redirect loop on reload
          localStorage.removeItem('user-store');
          if (window.location.pathname !== '/') {
            window.location.href = '/';
          }
          throw new Error('Unauthorized - session expired');
        }

        let errorMessage = `Request failed: ${response.status} ${response.statusText}`;

        try {
          const errorData = await response.text();
          if (errorData) {
            try {
              const jsonError = JSON.parse(errorData);
              errorMessage =
                jsonError.message || jsonError.error || errorMessage;
            } catch {
              errorMessage = errorData;
            }
          }
        } catch {
          // If we can't read the response body, use the status text
        }

        throw new Error(errorMessage);
      }

      if (response.status === 204 || response.status === 205) {
        return undefined as T;
      }

      const text = await response.text();

      if (!text.trim()) {
        return undefined as T;
      }

      const isoDateRegex =
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?([+-]\d{2}:\d{2}|Z)?$/;

      const reviver = (_key: string, value: unknown): unknown => {
        if (typeof value === 'string' && isoDateRegex.test(value)) {
          const dateTime = DateTime.fromISO(value).toLocal();
          return dateTime.isValid ? dateTime : value;
        }
        return value;
      };

      return JSON.parse(text, reviver) as T;
    } catch (error) {
      if (error instanceof Error) {
        throw error;
      }
      throw new Error('Network error or unexpected response format');
    }
  }
}
